// ABOUTME: Email preferences management page allowing users to control which email notifications they receive
// ABOUTME: Provides categorized toggles for optional email types with auto-save functionality

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Container,
  Divider,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import {
  NotificationsActive,
  Notifications,
  TrendingUp,
  ChildCare
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../hooks/useImpersonation';
import api from '../services/api';

interface EmailPreference {
  emailTypeId: number;
  emailTypeName: string;
  displayName: string;
  description: string;
  category: string;
  isEnabled: boolean;
  sortOrder: number;
}

const EmailPreferencesPage: React.FC = () => {
  const { user } = useAuth();
  const { isImpersonating, impersonatedUser } = useImpersonation();
  const [preferences, setPreferences] = useState<EmailPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);

  // Use impersonated user ID when impersonating, otherwise use admin's ID
  const effectiveUserId = isImpersonating && impersonatedUser ? impersonatedUser.userId : user?.userId;

  useEffect(() => {
    const fetchPreferences = async () => {
      if (!effectiveUserId) return;

      try {
        setLoading(true);
        const response = await api.get<EmailPreference[]>(`/api/users/${effectiveUserId}/email-preferences`);
        setPreferences(response.data);
      } catch (err) {
        console.error('Failed to fetch email preferences:', err);
        setError('Failed to load email preferences. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [effectiveUserId]);

  const handleTogglePreference = async (emailTypeName: string, newValue: boolean) => {
    if (!effectiveUserId || updating) return;

    setUpdating(emailTypeName);
    setError(null);

    try {
      await api.put(`/api/users/${effectiveUserId}/email-preferences/${emailTypeName}`, {
        isEnabled: newValue
      });

      // Update local state
      setPreferences(prev => 
        prev.map(pref => 
          pref.emailTypeName === emailTypeName 
            ? { ...pref, isEnabled: newValue }
            : pref
        )
      );
    } catch (err) {
      console.error('Failed to update email preference:', err);
      setError('Failed to update email preference. Please try again.');
    } finally {
      setUpdating(null);
    }
  };

  const groupedPreferences = preferences.reduce((groups, preference) => {
    const category = preference.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(preference);
    return groups;
  }, {} as Record<string, EmailPreference[]>);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Child Activity':
        return <ChildCare />;
      case 'Reports':
        return <TrendingUp />;
      default:
        return <Notifications />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 3 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            {/* Icon glyph sizing via fontSize, not a Typography text override */}
            <NotificationsActive sx={{ fontSize: 32, mr: 1, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Email Preferences
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Choose which email notifications you&apos;d like to receive. You can change these settings at any time.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          {Object.entries(groupedPreferences).map(([category, categoryPreferences]) => (
            <Card key={category} elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {getCategoryIcon(category)}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {category}
                  </Typography>
                </Box>
                
                <Stack spacing={2}>
                  {categoryPreferences
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((preference) => (
                      <Box key={preference.emailTypeId}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {preference.displayName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {preference.description}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                            {updating === preference.emailTypeName && (
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                            )}
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={preference.isEnabled}
                                  onChange={(e) => handleTogglePreference(preference.emailTypeName, e.target.checked)}
                                  disabled={updating === preference.emailTypeName}
                                  color="primary"
                                />
                              }
                              label={preference.isEnabled ? 'Enabled' : 'Disabled'}
                              sx={{ m: 0 }}
                            />
                          </Box>
                        </Box>
                        
                        {categoryPreferences.indexOf(preference) < categoryPreferences.length - 1 && (
                          <Divider sx={{ mt: 2 }} />
                        )}
                      </Box>
                    ))}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>

        {preferences.length === 0 && !loading && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No email preferences available
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Your email preferences will appear here once they&apos;re configured.
            </Typography>
          </Paper>
        )}

        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> Some emails like password resets, account security notifications, and important system updates cannot be disabled and will always be sent.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default EmailPreferencesPage;